/**
 *
 * Module:        Component
 * Name:          Guru Chat List
 * Description:   Renders pinned and chronological Guru chat sections
 * Developers:    Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu, Daniela Popescu
 * Copyright:     Copyright 2026 by Qalgo GmbH (www.qalgo.de). All rights reserved.
 *
 */

import React, {useState} from 'react';
import {
	Box,
	List,
	ListItemButton,
	ListItemText,
	Typography
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import GuruChatItemMenu from './GuruChatItemMenu';
import GuruSidebarSectionHeader from './GuruSidebarSectionHeader';
import QeeTooltip from '../qeeTooltip';
import {groupRecentGuruChats} from './guruChatHistoryModel';
import {GURU_BODY_FONT_SIZE, GURU_META_FONT_SIZE} from './guruTypography';

export function GuruChatRows({chats, currentChatId, labels, busy, onSelect, onRename, onTogglePin, onDelete}) {
	return (
		<List disablePadding>
			{chats.map(chat => (
				<Box key={chat.id} sx={{display: 'flex', alignItems: 'center', mx: 0.75, mb: 0.25,
					borderRadius: 1.5, bgcolor: chat.id === currentChatId ? 'action.selected' : 'transparent'}}>
					<ListItemButton
						selected={chat.id === currentChatId}
						disabled={busy}
						onClick={() => onSelect(chat)}
						sx={{px: 1.25, py: 0.55, minHeight: 42, minWidth: 0, borderRadius: 1.5,
							'&.Mui-selected': {bgcolor: 'transparent'}, '&.Mui-selected:hover': {bgcolor: 'action.hover'}}}
					>
						{chat.pinned && <PushPinIcon sx={{fontSize: 15, mr: 0.75, color: 'text.secondary'}} />}
						<QeeTooltip title={chat.title || labels.untitledChat}>
							<ListItemText
								primary={chat.title || labels.untitledChat}
								primaryTypographyProps={{
									noWrap: true,
									fontSize: GURU_BODY_FONT_SIZE,
									fontWeight: 400
								}}
							/>
						</QeeTooltip>
					</ListItemButton>
					<GuruChatItemMenu
						chat={chat}
						labels={labels}
						disabled={busy}
						onRename={onRename}
						onTogglePin={onTogglePin}
						onDelete={onDelete}
					/>
				</Box>
			))}
		</List>
	);
}

export default function GuruChatList(props) {
	const {chats, labels, recentExpanded, onRecentExpandedChange} = props;
	const [pinnedExpanded, setPinnedExpanded] = useState(true);
	const pinned = chats.filter(chat => chat.pinned)
		.sort((left, right) => String(right.pinnedAt || '').localeCompare(String(left.pinnedAt || '')));
	const recent = chats.filter(chat => !chat.pinned);
	const sections = groupRecentGuruChats(recent, labels);

	if (!chats.length) {
		return <Typography color="text.secondary" sx={{px: 2, py: 3}}>{labels.noChats}</Typography>;
	}

	return (
		<Box>
			{pinned.length > 0 && (
				<Box sx={{mb: 1}}>
					<GuruSidebarSectionHeader label={labels.pinnedChats} expanded={pinnedExpanded} onToggle={() => setPinnedExpanded(value => !value)} />
					{pinnedExpanded && <GuruChatRows {...props} chats={pinned} />}
				</Box>
			)}
			<GuruSidebarSectionHeader
				label={labels.recentChats}
				expanded={recentExpanded}
				onToggle={() => onRecentExpandedChange(!recentExpanded)}
			/>
			{recentExpanded && sections.map(section => (
				<Box key={section.label}>
					{sections.length > 1 && (
						<Typography sx={{px: 2, pt: 0.9, pb: 0.35, fontSize: GURU_META_FONT_SIZE, fontWeight: 500, color: 'text.disabled'}}>
							{section.label}
						</Typography>
					)}
					<GuruChatRows {...props} chats={section.chats} />
				</Box>
			))}
		</Box>
	);
}
